import { FC, Ref } from 'react';
import { css } from '@emotion/react';
import composeRefs from '@seznam/compose-react-refs';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { NakedButton, Text, useTooltip } from '@frontend/design-system';
import { WEAVE_COPILOT_HOTKEY } from '../../constant';
import { useChatbotAssistant } from '../../provider/chatbot-assistant.provider';
import { getWeaveCopilotTrackingId, WEAVE_COPILOT_ACTIONS } from '../../tracking-ids';
import { AssistantCollapseBadge, AssistantStatusBadge } from '../assistant-status-badge';

export const TriggerButton: FC<{ innerRef: Ref<HTMLButtonElement> }> = ({ innerRef }) => {
  const { t } = useTranslation('aiOnboarding');
  const { isOpen, toggleChatbotOpen, isFirstTimeInteracted } = useChatbotAssistant();

  const { Tooltip, tooltipProps, triggerProps } = useTooltip({
    placement: 'left',
  });

  const isMac = navigator.userAgent.includes('Mac');
  // Show tooltip for close by shortcut when the chatbot is open for the first time
  const showShortcutTooltip = isFirstTimeInteracted && isOpen;

  return (
    <>
      <NakedButton
        {...triggerProps}
        ref={composeRefs(innerRef, triggerProps.ref as any)}
        css={[styles.container, isOpen && styles.gradientBorder]}
        onClick={toggleChatbotOpen}
        aria-label={isOpen ? t('Close Chatbot') : t('Open Chatbot')}
        trackingId={getWeaveCopilotTrackingId({
          source: 'top-bar',
          action: isOpen ? WEAVE_COPILOT_ACTIONS.closeButton : WEAVE_COPILOT_ACTIONS.openButton,
        })}
      >
        {isOpen ? <AssistantCollapseBadge /> : <AssistantStatusBadge />}
      </NakedButton>
      <Tooltip {...tooltipProps} isOpen={showShortcutTooltip}>
        <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
          <Text as='span' color='white'>
            {t('Minimize AI Chat')}
          </Text>
          <Text as='span' color='subdued'>
            {isMac ? WEAVE_COPILOT_HOTKEY.labelMac : WEAVE_COPILOT_HOTKEY.labelWindows}
          </Text>
        </div>
      </Tooltip>
    </>
  );
};

const styles = {
  container: css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid ${theme.colors.neutral20};
    border-radius: ${theme.borderRadius.large};
    padding: ${theme.spacing(1)};
    width: 48px;
    height: 48px;
    cursor: pointer;
  `,
  gradientBorder: css`
    border: none;
    background: ${theme.colors.white};

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      padding: 2px;
      background: linear-gradient(
        45.06deg,
        #ff7301 2.26%,
        ${theme.colors.secondary.eggplant50} 50%,
        ${theme.colors.secondary.seaweed50} 97.74%
      );
      border-radius: ${theme.borderRadius.large};
      mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      mask-composite: xor;
      -webkit-mask-composite: xor;
    }
  `,
};
